<template>
  <section class="settings-card">
    <div class="card-header">
      <h2 class="card-title">算分规则</h2>
    </div>
    <p class="card-desc" style="margin-bottom: 20px;">
      项目默认算分口径；单份问卷可在问卷设置里通过 <code>settings_json.scoringRule</code> 覆盖。
    </p>

    <el-form :model="form" ref="form" label-position="top" class="info-form">
      <el-form-item label="规则">
        <el-select
          v-model="form.scoringRuleCode"
          placeholder="请选择算分规则"
          style="width: 360px;"
        >
          <el-option
            v-for="r in ruleOptions"
            :key="r.code || 'default'"
            :label="r.label"
            :value="r.code"
          >
            <span>{{ r.label }}</span>
            <span style="float: right; color: #8492a6; font-size: 12px;">{{ r.code || 'simple' }}</span>
          </el-option>
        </el-select>
      </el-form-item>

      <div class="form-tip" v-if="currentRule">
        <i class="el-icon-info"></i>
        <strong>{{ currentRule.label }}</strong> ·
        <span style="color:#334155;">{{ currentRule.desc }}</span>
        <div v-if="currentRule.formula" style="margin-top:6px;">
          公式：<code>{{ currentRule.formula }}</code>
        </div>
      </div>

      <div class="form-actions">
        <el-button
          type="primary"
          class="save-btn"
          :loading="saving"
          @click="saveSettings"
        >保存算分规则</el-button>
      </div>
    </el-form>
  </section>
</template>

<script>
import { getProjectDetail, updateProject } from 'index/api/project/manager'

const RULE_OPTIONS = [
  {
    code: 'simple',
    label: '原始总分（simple · 默认）',
    desc: 'final = total_score。不做归一，也无加分题概念。兼容老问卷。',
    formula: 'final = total_score'
  },
  {
    code: 'percentage',
    label: '百分比归一（percentage）',
    desc: '按百分制展示，扣除“不涉及”题目后归一到 100 分制。',
    formula: 'final = total_score / (full_score − na_excluded) × 100'
  },
  {
    code: 'bonusNormalized',
    label: '加分题 + 百分制（bonusNormalized）',
    desc: '常规题走百分制归一后叠加加分题得分；加分题可标“不涉及”且不计入 reviewed_item_count。',
    formula: 'final = bonus_score + (regular_score / (regular_full − regular_na)) × 100'
  },
  {
    code: 'custom',
    label: '自定义（custom · 预留）',
    desc: '接口预留；当前无具体规则实现，后端会兜底走 simple 计算。',
    formula: ''
  }
]

export default {
  name: 'ScoringRuleSection',
  props: {
    projectId: { type: [String, Number], required: true }
  },
  data() {
    return {
      saving: false,
      // 与 ExamSection 相同策略：缓存现有字段，保存时一并回传避免 Mapster Adapt 覆盖
      projectRaw: {},
      form: {
        scoringRuleCode: ''
      },
      ruleOptions: RULE_OPTIONS
    }
  },
  computed: {
    currentRule() {
      const code = this.form.scoringRuleCode || ''
      // 空串 / simple 两个都映射到 simple 说明项（跳过空占位项自身）
      if (!code) return RULE_OPTIONS.find(r => r.code === 'simple')
      return RULE_OPTIONS.find(r => r.code === code)
    }
  },
  mounted() {
    this.loadProject()
  },
  watch: {
    // projectId 由父组件异步路由传入；变化时重新加载
    projectId: {
      handler(newVal) {
        if (newVal) this.loadProject()
      },
      immediate: false
    }
  },
  methods: {
    async loadProject() {
      if (!this.projectId) return
      try {
        const res = await getProjectDetail(this.projectId)
        // 兼容多种响应结构（Furion 包裹 / 直接返回）
        const data = res?.data?.data || res?.data || res
        console.log('[ScoringRuleSection] loadProject raw response:', res)
        console.log('[ScoringRuleSection] extracted data:', data)
        if (data) this.applyData(data)
      } catch (e) {
        console.error('[ScoringRuleSection] 加载项目详情失败', e)
        const data = e?.data?.data || e?.data
        if (data) this.applyData(data)
      }
    },

    applyData(data) {
      // 缓存现有字段，保存时一并回传避免 Mapster Adapt 把其他字段覆盖为 null/默认值
      this.projectRaw = {
        name: data.name || data.Name,
        code: data.code || data.Code,
        customerName: data.customerName || data.CustomerName || data.customer_name,
        description: data.description || data.Description || data.remark,
        status: data.status || data.Status || 1,
        startTime: data.startTime || data.StartTime || data.start_time,
        endTime: data.endTime || data.EndTime || data.end_time,
        targetQuota: data.targetQuota || data.TargetQuota || data.target_quota,
        requireExam: data.requireExam ?? data.RequireExam ?? data.require_exam ?? 0,
        examFormKey: data.examFormKey || data.ExamFormKey || data.exam_form_key || null,
        examPassScore: data.examPassScore ?? data.ExamPassScore ?? data.exam_pass_score ?? 60,
        examSmsEnabled: data.examSmsEnabled ?? data.ExamSmsEnabled ?? data.exam_sms_enabled ?? 0,
        examAgreementPopup: data.examAgreementPopup ?? data.ExamAgreementPopup ?? data.exam_agreement_popup ?? 0,
        examMaxAttempts: data.examMaxAttempts ?? data.ExamMaxAttempts ?? data.exam_max_attempts ?? 0
      }
      // 使用 $set 确保响应式（保险起见，实测有偶发不刷新的场景）
      const code = data.scoringRuleCode || data.ScoringRuleCode || data.scoring_rule_code || ''
      this.$set(this.form, 'scoringRuleCode', code)
      console.log('[ScoringRuleSection] form.scoringRuleCode set to:', code)
    },

    async saveSettings() {
      this.saving = true
      try {
        await updateProject({
          id: this.projectId,
          // 合并原有字段避免被 Mapster Adapt 覆盖为 null/默认值
          name: this.projectRaw.name,
          code: this.projectRaw.code,
          customerName: this.projectRaw.customerName,
          description: this.projectRaw.description,
          status: this.projectRaw.status,
          startTime: this.projectRaw.startTime,
          endTime: this.projectRaw.endTime,
          targetQuota: this.projectRaw.targetQuota,
          requireExam: this.projectRaw.requireExam,
          examFormKey: this.projectRaw.examFormKey || null,
          examPassScore: this.projectRaw.examPassScore || 60,
          examSmsEnabled: this.projectRaw.examSmsEnabled,
          examAgreementPopup: this.projectRaw.examAgreementPopup,
          examMaxAttempts: this.projectRaw.examMaxAttempts || 0,
          // 本组件负责的字段（空串=走 simple 默认）
          scoringRuleCode: this.form.scoringRuleCode || ''
        })
        this.$message.success('保存成功')
      } catch (e) {
        this.$message.error(e?.data?.message || e?.message || '保存失败')
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.form-tip {
  margin: 8px 0 16px;
  padding: 10px 14px;
  background: #f0f7ff;
  border-radius: 8px;
  font-size: 12px;
  color: #1476ff;

  i { margin-right: 6px; }

  code {
    background: #fff;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 12px;
    color: #334155;
  }
}

.card-desc code {
  background: #f5f7fa;
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 12px;
  color: #475569;
}
</style>
